import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/authStore';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardLayout from './components/dashboard/DashboardLayout';
import DashboardHome from './pages/dashboard/DashboardHome';
import StoreSettings from './pages/dashboard/StoreSettings';
import Categories from './pages/dashboard/Categories';
import Products from './pages/dashboard/Products';
import Orders from './pages/dashboard/Orders';
import DesignSettings from './pages/dashboard/DesignSettings';
import ThemeEditor from './pages/dashboard/ThemeEditor';
import PromoCodes from './pages/dashboard/PromoCodes';
import DeliveryAreas from './pages/dashboard/DeliveryAreas';
import RidersDelivery from './pages/dashboard/RidersDelivery';
import Branches from './pages/dashboard/Branches';
import PaymentGateways from './pages/dashboard/PaymentGateways';
import MyFatoorah from './pages/dashboard/MyFatoorah';
import Team from './pages/dashboard/Team';
import Customers from './pages/dashboard/Customers';
import ApiAccess from './pages/dashboard/ApiAccess';
import Deliveries from './pages/dashboard/Deliveries';
import PointOfSale from './pages/dashboard/PointOfSale';
import SubscriptionPage from './pages/dashboard/SubscriptionPage';
import SubscriptionCallback from './pages/dashboard/SubscriptionCallback';
import CbkCallback from './pages/dashboard/CbkCallback';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminStores from './pages/admin/AdminStores';
import AdminPayments from './pages/admin/AdminPayments';
import AdminPlanSettings from './pages/admin/AdminPlanSettings';
import AdminPromoCodes from './pages/admin/AdminPromoCodes';
import AdminSettings from './pages/admin/AdminSettings';
import StorefrontPage from './pages/StorefrontPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import SetPasswordPage from './pages/SetPasswordPage';
import AutoLoginPage from './pages/AutoLoginPage';
import PaymentLinkPage from './pages/PaymentLinkPage';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.role !== 'admin') return <Navigate to="/dashboard" />;
  return <>{children}</>;
}

function SlugRedirect() {
  const { slug } = useParams();
  const reserved = ['login', 'register', 'dashboard', 'admin', 'store', 'terms', 'privacy', 'auto-login', 'set-password', 'pay'];
  if (slug && !reserved.includes(slug)) {
    return <Navigate to={`/store/${slug}`} replace />;
  }
  return <Navigate to="/" replace />;
}

function SubdomainStorefront({ slug, isCustomDomain }: { slug: string; isCustomDomain?: boolean }) {
  const params = useParams();
  return <StorefrontPage slug={slug} subdomainWildcard={params['*'] || ''} customDomain={isCustomDomain ? slug : undefined} />;
}

function App() {
  const { loadUser, isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated) {
      loadUser();
    }
  }, []);

  // Check if we're on a subdomain (*.cartz.cc) or custom domain
  const hostname = window.location.hostname;
  const isCartz = hostname.endsWith('.cartz.cc') || hostname === 'cartz.cc' || hostname === 'localhost';
  const isSubdomain = hostname.endsWith('.cartz.cc') && !hostname.startsWith('www.');
  const isCustomDomain = !isCartz && hostname !== 'localhost';

  if (isSubdomain) {
    const subSlug = hostname.split('.')[0];
    return (
      <Routes>
        <Route path="/pay/:token" element={<PaymentLinkPage />} />
        <Route path="/store/:slug/*" element={<StorefrontPage />} />
        <Route path="/*" element={<SubdomainStorefront slug={subSlug} />} />
      </Routes>
    );
  }

  if (isCustomDomain) {
    // Custom domain — pass hostname, StorefrontPage will resolve slug via API
    return (
      <Routes>
        <Route path="/pay/:token" element={<PaymentLinkPage />} />
        <Route path="/store/:slug/*" element={<StorefrontPage />} />
        <Route path="/*" element={<SubdomainStorefront slug={hostname} isCustomDomain />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/auto-login" element={<AutoLoginPage />} />
      <Route path="/store/:slug/*" element={<StorefrontPage />} />
      <Route path="/pay/:token" element={<PaymentLinkPage />} />

      <Route path="/dashboard" element={<PrivateRoute><DashboardLayout /></PrivateRoute>}>
        <Route index element={<DashboardHome />} />
        <Route path="settings" element={<StoreSettings />} />
        <Route path="design" element={<DesignSettings />} />
        <Route path="theme-editor" element={<ThemeEditor />} />
        <Route path="categories" element={<Categories />} />
        <Route path="products" element={<Products />} />
        <Route path="orders" element={<Orders />} />
        <Route path="pos" element={<PointOfSale />} />
        <Route path="deliveries" element={<Deliveries />} />
        <Route path="promo-codes" element={<PromoCodes />} />
        <Route path="branches" element={<Branches />} />
        <Route path="payment-gateways" element={<PaymentGateways />} />
        <Route path="myfatoorah" element={<MyFatoorah />} />
        <Route path="delivery-areas" element={<DeliveryAreas />} />
        <Route path="riders-delivery" element={<RidersDelivery />} />
        <Route path="customers" element={<Customers />} />
        <Route path="api-access" element={<ApiAccess />} />
        <Route path="team" element={<Team />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="subscription/callback" element={<SubscriptionCallback />} />
        <Route path="subscription/cbk-callback" element={<CbkCallback />} />
      </Route>

      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="stores" element={<AdminStores />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="plan-settings" element={<AdminPlanSettings />} />
        <Route path="promo-codes" element={<AdminPromoCodes />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      <Route path="/:slug" element={<SlugRedirect />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default App;
